import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { fetchCourse } from "@/lib/logicApi";
import { fetchTrackRecords } from "@/lib/academyProgress";
import { GeometricEye, LogicImage } from "./LogicImage";
import { useLogicUi } from "./logicUi";
import { useSEO } from "@/lib/useSEO";
import { DisciplineSwitcher } from "@/components/academy/DisciplineSwitcher";
import { LockBadge } from "@/components/academy/TrialGate";
import { StatusBadge, PhaseProgress } from "@/components/academy/LessonNav";
import { LessonVirtueSeal } from "@/components/academy/LessonVirtueSeal";
import { BTN_FX, DISCIPLINE_THEME } from "@/lib/academyTheme";

const ROMAN = ["I", "II", "III", "IV", "V", "VI"];

export default function LogicPhase() {
  const { ui, L, t } = useLogicUi();
  const th = DISCIPLINE_THEME.logic;
  const { phaseSlug } = useParams();
  const [course, setCourse] = useState(null);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    fetchCourse().then(setCourse).catch(() => {});
    fetchTrackRecords("logic").then(setRecords);
  }, [phaseSlug]);

  const phase = course?.phases?.find((p) => p.slug === phaseSlug);
  useSEO({
    title: phase ? `${L(phase.title)} | Stoic Logic | StoicOs.ai Academy` : "Stoic Logic",
    description: phase ? L(phase.centralQuestion) : "",
    canonical: `https://stoicos.ai/academy/logic/phase/${phaseSlug}`,
  });

  if (!course) return <div className="min-h-screen bg-ink" />;
  if (!phase) return <Navigate to="/academy/logic" replace />;
  const media = course.media || {};

  return (
    <main className="min-h-screen bg-ink px-6 sm:px-10 py-24" data-testid="logic-phase-page">
      <div className="max-w-4xl mx-auto">
        <DisciplineSwitcher active="logic" />
        <nav aria-label="Breadcrumb" className="text-[10px] tracking-[0.25em] uppercase text-[#8fa3c8] mb-10">
          <Link to="/academy" className="hover:text-gold">{ui.academyCrumb}</Link> / <Link to="/academy/logic" className="hover:text-gold">{L(course.title)}</Link> / <span className="text-fog">{L(phase.title)}</span>
        </nav>
        <div className="flex items-center gap-4 mb-6">
          <GeometricEye media={media} size={36} />
          <span className="font-serif text-3xl text-[#8fa3c8]">{ui.phase} {ROMAN[phase.order - 1]}</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl text-bone">{L(phase.title)}</h1>
        <p className="mt-6 font-serif italic text-xl text-gold/90 max-w-2xl">{L(phase.centralQuestion)}</p>
        <p className="mt-4 text-fog font-reading max-w-2xl leading-[1.9]">{ui.coreAbility} {L(phase.coreAbility)}</p>
        <p className="mt-3"><PhaseProgress phase={phase} records={records} accent="#8fa3c8" /></p>

        <div className="mt-14 space-y-5">
          {phase.lessons.map((l) => {
            const rec = records.find((r) => r.lessonSlug === l.slug);
            return (
              <div key={l.slug} className="border border-[#c8a95a]/20 bg-[#171c26] hover:border-gold/50 transition-colors p-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#8fa3c8]">{ui.lesson} {l.order}</p>
                    <p className="font-serif text-xl text-bone mt-1">{L(l.title)}</p>
                  </div>
                  <StatusBadge rec={rec} accent="#8fa3c8" />
                </div>
                {course.access === "preview" ? (
                  <div className="mt-4">
                    <LockBadge className="mb-2 block" />
                    <Link to="/my-stoicos" data-testid={`logic-lesson-trial-${l.slug}`}
                      className={`inline-flex items-center justify-center gap-2 min-h-[48px] px-5 text-[11px] font-semibold tracking-[0.2em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>
                      {t("trial.cta")} <span aria-hidden>→</span>
                    </Link>
                  </div>
                ) : (<>
                  <Link to={`/academy/logic/phase/${phase.slug}/lesson/${l.slug}`} data-testid={`logic-lesson-link-${l.slug}`}
                    className={`mt-4 inline-flex items-center justify-center gap-2 min-h-[48px] px-5 text-[11px] font-semibold tracking-[0.2em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>
                    {t("academy.nav.openLesson")} <span aria-hidden>→</span>
                  </Link>
                  {l.locked && <LockBadge className="mt-2 block" />}
                </>)}
                <p className="text-sm text-fog/70 mt-3 font-reading italic">{L(l.thresholdSentence)}</p>
              </div>
            );
          })}
        </div>

        {phase.assetKey && (
          <div className="mt-16 max-w-xl"><LogicImage media={media} assetKey={phase.assetKey} alt="" /></div>
        )}
        <LessonVirtueSeal />
      </div>
    </main>
  );
}
